import SEO from '../components/SEO';

// Static content lists — hoisted to module scope so they don't allocate on every render.
const PERSONAL_INFO = [
  'Full Name', 'Email Address', 'Phone Number', 'Gender', 'Date of Birth',
  'City / Location', 'Team Name', 'Playing Position', 'Height',
  'Tournament Registration Information',
  'Any information voluntarily submitted through forms or profile updates'
];
const ORGANIZER_INFO = [
  'Organizer Name', 'Organization or Tournament Name', 'Contact Information',
  'Tournament Details', 'Registration Links and Related Information'
];
const AUTO_INFO = ['Device Information', 'Browser Type', 'IP Address', 'Pages Visited', 'Usage Data', 'Cookies and Similar Technologies'];
const USE_PURPOSES = [
  'Create and manage user accounts', 'Verify user identities', 'Facilitate tournament registrations',
  'Allow organizers to manage tournaments', 'Improve platform performance and security',
  'Respond to support requests', 'Communicate important account or tournament updates',
  'Prevent fraud, abuse, or unauthorized activity', 'Comply with legal obligations'
];
const SHARING_CASES = [
  'With tournament organizers for registration and participation purposes',
  'With service providers who help operate the platform',
  'To comply with legal obligations',
  'To protect the rights, safety, and security of users and the platform'
];
const COOKIE_USES = ['Maintain login sessions', 'Improve website functionality', 'Analyze usage patterns', 'Enhance user experience'];
const RETENTION_USES = ['Provide platform services', 'Meet legal obligations', 'Resolve disputes', 'Enforce platform policies'];

const Section = ({ n, title, children }) => (
  <section className="mt-10">
    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 tracking-tight">
      <span className="text-[#F97316] mr-2">{n}.</span>{title}
    </h2>
    <div className="space-y-3 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc list-outside pl-5 space-y-1.5 text-gray-700">
    {items.map((it) => <li key={it}>{it}</li>)}
  </ul>
);

const Privacy = () => (
  <>
    <SEO
      title="Privacy Policy"
      description="How Beyond Ball India collects, uses, stores, and protects information across our basketball tournament discovery and registration platform."
      path="/privacy"
    />

    <div className="pt-24 pb-16 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs font-semibold text-[#F97316] uppercase tracking-wider mb-2">Legal</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 mb-3">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last Updated: June 4, 2026</p>

        <div className="space-y-4 text-gray-700 leading-relaxed">
          <p>Welcome to Beyond Ball India ("Beyond Ball India", "we", "our", or "us"). This Privacy Policy explains how we collect, use, store, and protect information when you use our website, services, and platform located at beyondballindia.com.</p>
          <p>By using our platform, you agree to the practices described in this Privacy Policy.</p>
        </div>

        <Section n="1" title="Information we collect">
          <h3 className="font-semibold text-gray-900 mt-2">Information you provide</h3>
          <p>When you create an account, register for tournaments, or contact us, we may collect:</p>
          <Bullets items={PERSONAL_INFO} />

          <h3 className="font-semibold text-gray-900 mt-5">Organizer information</h3>
          <p>When organizers create accounts or list tournaments, we may collect:</p>
          <Bullets items={ORGANIZER_INFO} />

          <h3 className="font-semibold text-gray-900 mt-5">Automatically collected information</h3>
          <p>We may automatically collect:</p>
          <Bullets items={AUTO_INFO} />
        </Section>

        <Section n="2" title="How we use information">
          <p>We use collected information to:</p>
          <Bullets items={USE_PURPOSES} />
        </Section>

        <Section n="3" title="Tournament registration information">
          <p>Registrations are collected and stored on Beyond Ball India. When you register for a tournament, we share your registration details (including team roster and payment proof, if applicable) with the tournament organizer so they can verify and manage your entry.</p>
          <p>Payment screenshots and UPI QR images uploaded to the platform are stored securely and are only accessible to the tournament organizer, the registrant, and Beyond Ball India administrators.</p>
        </Section>

        <Section n="4" title="Account security">
          <p>We take reasonable technical and organizational measures to protect user information.</p>
          <p>However, no online platform can guarantee absolute security. Users are responsible for maintaining the confidentiality of their login credentials.</p>
        </Section>

        <Section n="5" title="Data sharing">
          <p className="font-semibold text-gray-900">We do not sell personal information.</p>
          <p>We may share information only in the following situations:</p>
          <Bullets items={SHARING_CASES} />
        </Section>

        <Section n="6" title="User profiles">
          <p>Player profiles are private by default and are only accessible by the account owner unless otherwise stated on the platform.</p>
          <p>We may introduce public profile features in the future. If public profiles are introduced, users will be informed before any profile information becomes publicly visible.</p>
        </Section>

        <Section n="7" title="Cookies">
          <p>We may use cookies and similar technologies to:</p>
          <Bullets items={COOKIE_USES} />
          <p>Users may disable cookies through their browser settings, though some features may not function properly.</p>
        </Section>

        <Section n="8" title="Data retention">
          <p>We retain information only as long as necessary to:</p>
          <Bullets items={RETENTION_USES} />
          <p>Users may request account deletion at any time.</p>
        </Section>

        <Section n="9" title="Account deletion">
          <p>Users may request deletion of their account and associated data through their account settings or by contacting us.</p>
          <p>Some information may be retained where required by law or for legitimate operational purposes.</p>
        </Section>

        <Section n="10" title="Children's privacy">
          <p>The platform may be used by youth basketball players participating in tournaments.</p>
          <p>If a parent or guardian believes that personal information has been provided improperly, they may contact us and request review or deletion of the information.</p>
        </Section>

        <Section n="11" title="Third-party links">
          <p>Our platform may contain links to external websites, registration forms, social media platforms, or other third-party services.</p>
          <p>We are not responsible for the privacy practices or content of those third-party services.</p>
        </Section>

        <Section n="12" title="Changes to this privacy policy">
          <p>We may update this Privacy Policy periodically.</p>
          <p>Any changes will be posted on this page along with an updated revision date.</p>
          <p>Continued use of the platform after updates constitutes acceptance of the revised policy.</p>
        </Section>

        <Section n="13" title="Contact us">
          <p>For questions regarding this Privacy Policy or your personal information, please contact:</p>
          <div className="bg-white border border-gray-200 rounded-2xl p-5 mt-3">
            <p className="font-semibold text-gray-900 mb-1">Beyond Ball India</p>
            <p>Website: <a href="https://beyondballindia.com" className="text-[#F97316] hover:text-orange-700 font-semibold">beyondballindia.com</a></p>
            <p>Email: <a href="mailto:beyondballindia@gmail.com" className="text-[#F97316] hover:text-orange-700 font-semibold">beyondballindia@gmail.com</a></p>
          </div>
        </Section>
      </div>
    </div>
  </>
);

export default Privacy;
